
/* injects from baggage-loader */
require('./style.styl');

import React from 'react'
import PropTypes from 'prop-types'
import isFunction from 'lodash/isFunction'
import {StickyContainer, Sticky} from 'react-sticky'
import {CSSTransitionGroup} from 'react-transition-group'
import ContainerDimentionsContext from 'components/ContainerDimentionsContext'
import ProgressWithContext from 'components/Progress/ProgressWithContext'
import Icon from 'components/Icon'
import Loader from 'components/Loader'
import classnames from 'classnames'
import pluralize from 'pluralize'
import partial from 'utils/partial'
import SiteCreation from './SiteCreation'
import MarketingChannels from './MarketingChannels'
import Mappings from './Mappings'
import Installation from './Installation'
import instructions from './instructions.json'
import findLast from 'lodash/findLast'
import findindex from 'lodash/findIndex'
import findLastIndex from 'lodash/findLastIndex'

const stepsConfig = [
  {
    id: 'siteCreation',
    title: 'How do you want to install Divvit?',
    desc: `
      Please choose how you want to install Divvit: Google Tag Manager, or by manually adding the script to your site.
    `,
    component: SiteCreation,
    doneText: 'Continue',
    highlights: [
      'With your site info, we will create your personal tracking code',
      'Pick what time zone and currency you want data displayed in',
      'When installed, start saving time with accessible data you don’t have to be an expert to master'
    ]
  },
  {
    id: 'marketingChannels',
    title: 'Connect your marketing channels',
    desc: 'By connecting your marketing channels, we can automatically import your data to calculate your ROI.',
    component: MarketingChannels,
    doneText: 'I\'m done adding',
    highlights: [
      'Connecting your channels will make data available in your Divvit account',
      'You can add all other channels in the Divvit app later',
      'By adding channels, you get exact marketing ROI insights to keep your store running efficiently'
    ]
  },
  {
    id: 'mappings',
    title: 'What\'s your checkout URL?',
    desc: `
      By entering your checkout URL, you allow us to build a sales funnel and
      show exactly how many customers leave your site during each step of your customer journey.
    `,
    component: Mappings,
    doneText: 'I entered my URL',
    highlights: [
      'We build your personal sales funnel just for your site with your checkout URL',
      'You can see how users behave from the first page all the way through conversion',
      'Get exact stats on how many customers enter your checkout, and how many leave without buying'
    ]
  },
  {
    id: 'installation',
    title: ({platform}) => (
      platform ? `Install ${instructions[platform] && instructions[platform].title}` : 'Installation'
    ),
    desc: 'Once installed, we will start receiving and preparing your data into reports.',
    component: Installation,
    doneText: 'Go to Divvit!',
    highlights: [
      'Once installed, we will start collecting data and you’ll go into the Divvit Dashboard',
      'You’ve also received these instructions via email',
      'Please allow a few minutes to start seeing the first visits come in',
      'You’ll be all set up for receiving amazing insights!'
    ]
  }
]

export class Wizard extends React.Component {
  static propTypes = {
    onSiteCreationSubmit: PropTypes.func,
    onSaveWizardState: PropTypes.func,
    onClearStorage: PropTypes.func,
    onAddMappingForCategory: PropTypes.func,
    // openBillingPlansModal: PropTypes.func,
    // merchant: PropTypes.object,
    site: PropTypes.object,
    isBusy: PropTypes.bool,
    // disableBillingPopup: PropTypes.bool,
    pushState: PropTypes.func,
    channels: PropTypes.array,
    wizard: PropTypes.object,
    formValues: PropTypes.object,
    currentUser: PropTypes.object,
    queryError: PropTypes.string,
    newMode: PropTypes.string,
    location: PropTypes.object,
    disableSteps: PropTypes.array,
    disabledFields: PropTypes.array,
    embedded: PropTypes.oneOfType([PropTypes.bool, PropTypes.string])
  }

  static defaultProps = {
    site: {}
  }

  render() {
    const {currentUser} = this.props
    const steps = this.getSteps()

    return (
      <StickyContainer className='wizard'>
        <div className='wizard_title'>
          <h1>Welcome! Let{"'"}s get you started</h1>
        </div>
        <div className='wizard_container'>
          <div className='wizard_steps'>
            {currentUser && steps ? steps.map(this.renderStep) : <Loader />}
          </div>
          <div className='wizard_legend'>
            <Sticky topOffset={60}>
              {({style, isSticky}) => {
                return (
                  <div style={style} className={isSticky ? 'wizard_legend_sticky' : null}>
                    {this.renderLegend()}
                  </div>
                )
              }}
            </Sticky>
          </div>
        </div>
      </StickyContainer>
    )
  }

  renderStep = (step, index) => {
    const {component: Component, title, desc, doneText} = step
    const {
      channels,
      site,
      formValues,
      isBusy,
      queryError,
      location,
      disabledFields,
      embedded
    } = this.props
    const {active, completed, last} = this.getStepStatus(step, index)
    const classNames = classnames('wizard_step', {
      'is-active': active,
      'is-completed': completed
    })

    return (
      <div className={classNames} key={index} onClick={completed ? partial(this.switchStep, step, index) : undefined}>
        <div className='wizard_step_number'>{index + 1}</div>
        <h1>{isFunction(title) ? title(site || {}) : title}</h1>
        <p className='wizard_step_desc'>{desc}</p>
        <CSSTransitionGroup
          className='wizard_step_container'
          transitionName='appear'
          transitionEnterTimeout={500}
          transitionLeaveTimeout={500}
        >
          {(Component && active)
            ? <Component
              onSubmit={partial(this.nextStep, step)}
              onSkip={partial(this.skipStep, step)}
              submitButtonText={isFunction(doneText) ? doneText(last) : doneText}
              site={site}
              channels={channels}
              disabledFields={disabledFields}
              formValues={formValues}
              query={location && location.query}
              isBusy={isBusy}
              queryError={queryError}
              embedded={embedded}
            />
            : null
          }
        </CSSTransitionGroup>
      </div>
    )
  }

  renderLegend() {
    const steps = this.getSteps()

    if (!steps) return null

    const total = steps.length + 1
    const activeIndex = findLastIndex(steps, s => s.active)
    const step = steps[activeIndex]
    const done = steps.filter(s => s.active).length + 1
    const left = total - done
    return (
      <div className='wizard_progress'>
        <ContainerDimentionsContext>
          <ProgressWithContext
            className='wizard_progress_bar'
            current={done}
            limit={total}
            barFirst
            withoutValues
            currentValueLabel='Progress'
            totalValueLabel={left === 0 ? 'Last step!' : `${total - done} ${pluralize('steps', total - done)} left`}
          />
        </ContainerDimentionsContext>
        <div className='wizard_goals'>
          <div className='wizard_goals_icon'>
            <Icon className='wizard_goals_icon_svg' name='star' size='18px' />
          </div>
          <h2>What happens now?:</h2>
          {step && step.highlights.map((h, index) => (
            <p key={index}>{h}</p>
          ))}
        </div>
      </div>
    )
  }

  getStepStatus(step, index) {
    const steps = this.getSteps()

    if (!steps) return null

    const activeStep = findLast(steps, s => s.active) || steps[0]
    const activeIndex = findLastIndex(steps, s => s.active) || 0

    return {
      active: step.id === activeStep.id,
      completed: index < activeIndex,
      last: index === (steps.length - 1)
    }
  }

  getNextStep() {
    const steps = this.getSteps()

    if (!steps) return null

    const activeIndex = findLastIndex(steps, s => s.active) || 0
    const nextIndex = activeIndex + 1
    const completed = nextIndex >= steps.length

    return {
      completed,
      nextIndex: completed ? null : nextIndex
    }
  }

  getSteps() {
    const {wizard: {steps}, disableSteps} = this.props

    if (!steps) return null

    const filteredSteps = stepsConfig
      .filter(step => !disableSteps.includes(step.id))
      .map(step => ({
        ...step,
        ...steps.find(s => s.id === step.id)
      }))
    const {platform} = this.props.site || {}

    if (!platform) return filteredSteps

    return filteredSteps
  }

  saveWizardState({active, activeStepId, forceEdit, siteCreationMode}) {
    const {
      wizard: {steps, edit},
      onSaveWizardState,
      onClearStorage,
      // openBillingPlansModal,
      // disableBillingPopup,
      pushState,
      location
      // merchant
    } = this.props

    if ((active && forceEdit) || siteCreationMode) {
      const activeIndex = findindex(steps, s => s.id === activeStepId)
      return onSaveWizardState({
        active,
        edit: forceEdit,
        steps: steps.map((step, index) => ({
          ...step,
          active: index <= activeIndex
        }))
      })
    } else if (active) {
      return onSaveWizardState({
        active,
        edit,
        steps: steps.map(step => {
          if (step.id !== activeStepId) return step
          return {...step, active: true}
        })
      })
    } else {
      onSaveWizardState(null)
      return onClearStorage(['wizard', 'form_wizard', 'form_wizard-mappings'])
        .success(() => {
          const {redirectUrl} = location.query || {}
          if (redirectUrl) {
            pushState({pathname: location.query.redirectUrl, state: {consume: ['redirectUrl']}})
          } else {
            pushState({pathname: '/'})
          }
          // if (!disableBillingPopup && merchant && !merchant.activePlan) {
          //   openBillingPlansModal()
          // }
        })
    }
  }

  switchStep = (step, index) => {
    const {newMode} = this.props
    this.saveWizardState({
      active: !newMode,
      activeStepId: step.id,
      forceEdit: !newMode,
      siteCreationMode: newMode
    })
  }

  nextStep = (step, creds, dispatch) => {
    const {
      onSiteCreationSubmit,
      onAddMappingForCategory,
      newMode,
      pushState
    } = this.props
    const steps = this.getSteps()
    this.saveWizardState({
      active: !newMode,
      activeStepId: step.id,
      siteCreationMode: newMode
    })
    if (step.id === 'siteCreation') {
      onSiteCreationSubmit(creds, dispatch)
        .then(({site}) => {
          const {completed, nextIndex} = this.getNextStep()
          this.saveWizardState({
            active: newMode ? false : !completed,
            activeStepId: nextIndex && steps[nextIndex].id,
            siteCreationMode: newMode
          })
          if (newMode && newMode === 'new') {
            pushState(`/wizard/${site.id}`)
          }
        })
    } else if (step.id === 'mappings') {
      onAddMappingForCategory(creds, dispatch)
        .then(() => {
          const {completed, nextIndex} = this.getNextStep()
          this.saveWizardState({
            active: !completed,
            activeStepId: nextIndex && steps[nextIndex].id,
            siteCreationMode: newMode
          })
        })
    } else {
      const {completed, nextIndex} = this.getNextStep()
      this.saveWizardState({
        active: !completed,
        activeStepId: nextIndex && steps[nextIndex].id,
        siteCreationMode: newMode ? !completed : false
      })
    }
  }

  skipStep = step => {
    const {newMode} = this.props
    const steps = this.getSteps()
    const {completed, nextIndex} = this.getNextStep()
    this.saveWizardState({
      active: !completed,
      activeStepId: nextIndex && steps[nextIndex].id,
      siteCreationMode: newMode
    })
  }
}

export default Wizard



// WEBPACK FOOTER //
// ./client/components/Wizard/index.js